// A scene graph based on the Canvas2D drawing API.

export class Scene {
  constructor(public backgroundColor: string, public root: Layer) {}
}

export class Layer {
  private _components = new Map<string, Component>();

  shape(): ShapeComponent | null {
    const result = this._components.get("Shape");
    return result ? (result as ShapeComponent) : null;
  }

  children(): ChildrenComponent | null {
    const result = this._components.get("Children");
    return result ? (result as ChildrenComponent) : null;
  }

  installComponent(component: Component) {
    this._components.set(component.type, component);
  }

  appendChild(child: Layer) {
    const childrenComponent = this.children();
    if (childrenComponent == null) {
      throw new Error(`Can't append child to node`);
    }
    childrenComponent.children.push(child);
  }

  // TODO: Add setters for shape, style, position
}

///////////////////////////////////////////////////////////////

export type RectShapeInfo = {
  type: "rect";
  x: number;
  y: number;
  width: number;
  height: number;
};

export type EllipseShapeInfo = {
  type: "ellipse";
  x: number;
  y: number;
  radiusX: number;
  radiusY: number;
  rotation: number;
  startAngle: number;
  endAngle: number;
};

export type TextShapeInfo = {
  type: "text";
  text: string;
  x: number;
  y: number;
  maxWidth?: number;
};

export type RayShapeInfo = {
  type: "ray";
  origin: { x: number; y: number };
  dest: { x: number; y: number };
};

export type CircleShapeInfo = {
  type: "circle";
  center: { x: number; y: number };
  radius: number;
};

export type LightbulbShapeInfo = {
  type: "lightbulb";
  center: { x: number; y: number };
  radius: number;
};

export type ShapeInfo =
  | RectShapeInfo
  | EllipseShapeInfo
  | TextShapeInfo
  | RayShapeInfo
  | CircleShapeInfo
  | LightbulbShapeInfo;

type Style = {
  fillStyle?: string;
  strokeStyle?: string;
  lineWidth?: number;
  font?: string;
};

///////////////////////////////////////////////////////////////

type ComponentType = "Shape" | "Children";

interface Component {
  type: ComponentType;
}

class ShapeComponent implements Component {
  public type: ComponentType = "Shape";
  constructor(public shape: ShapeInfo, public style: Style) {}
}

class ChildrenComponent implements Component {
  public type: ComponentType = "Children";

  constructor(public children: Layer[]) {}
}

///////////////////////////////////////////////////////////////

export class Group extends Layer {
  constructor(children: Layer[] = []) {
    super();
    this.installComponent(new ChildrenComponent(children));
  }
}

export class Shape extends Layer {
  constructor(shape: ShapeInfo, style: Style) {
    super();
    this.installComponent(new ShapeComponent(shape, style));
  }
}

export function drawScene(scene: Scene, ctx: CanvasRenderingContext2D) {
  ctx.fillStyle = scene.backgroundColor;
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  drawSceneHelper(scene.root, ctx);
}

const DRAW_DEBUG = false;

function drawSceneHelper(root: Layer, ctx: CanvasRenderingContext2D) {
  const shapeComponent = root.shape();
  if (shapeComponent != null) {
    const style = shapeComponent.style;
    ctx.fillStyle = style.fillStyle != null ? style.fillStyle : "transparent";
    ctx.strokeStyle =
      style.strokeStyle != null ? style.strokeStyle : "transparent";
    ctx.lineWidth = style.lineWidth != null ? style.lineWidth : 0;
    ctx.font = style.font != null ? style.font : "10px sans-serif";

    const shape = shapeComponent.shape;
    switch (shape.type) {
      case "rect": {
        if (style.fillStyle != null) {
          ctx.fillRect(shape.x, shape.y, shape.width, shape.height);
          DRAW_DEBUG && console.log(`ctx.fillRect`);
        }
        if (style.strokeStyle != null) {
          ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
          DRAW_DEBUG && console.log(`ctx.strokeRect`);
        }
        break;
      }
      case "ellipse": {
        ctx.beginPath();
        ctx.ellipse(
          shape.x,
          shape.y,
          shape.radiusX,
          shape.radiusY,
          shape.rotation,
          shape.startAngle,
          shape.endAngle
        );

        if (style.fillStyle != null) {
          ctx.fill();
        }
        if (style.strokeStyle != null) {
          ctx.stroke();
        }

        break;
      }
      case "text": {
        if (style.fillStyle != null) {
          ctx.fillText(shape.text, shape.x, shape.y, shape.maxWidth);
          DRAW_DEBUG && console.log(`ctx.fillText`);
        }
        if (style.strokeStyle != null) {
          ctx.strokeText(shape.text, shape.x, shape.y, shape.maxWidth);
          DRAW_DEBUG && console.log(`ctx.strokeText`);
        }
        break;
      }
      case "ray": {
        const origin = shape.origin;
        const dest = shape.dest;

        const fromx = origin.x;
        const fromy = origin.y;
        const tox = dest.x;
        const toy = dest.y;

        // From here: https://stackoverflow.com/a/6333775
        var headlenPx = 10;
        var dx = tox - fromx;
        var dy = toy - fromy;
        var angle = Math.atan2(dy, dx);

        ctx.beginPath();
        ctx.moveTo(fromx, fromy);
        ctx.lineTo(tox, toy);
        ctx.closePath();
        ctx.stroke();

        ctx.fillStyle = ctx.strokeStyle;
        ctx.beginPath();
        ctx.moveTo(tox, toy);
        ctx.lineTo(
          tox - headlenPx * Math.cos(angle - Math.PI / 5),
          toy - headlenPx * Math.sin(angle - Math.PI / 5)
        );
        ctx.lineTo(
          tox - headlenPx * Math.cos(angle + Math.PI / 5),
          toy - headlenPx * Math.sin(angle + Math.PI / 5)
        );
        ctx.lineTo(tox, toy);
        ctx.fill();
        break;
      }
      case "circle": {
        ctx.beginPath();
        ctx.ellipse(
          shape.center.x,
          shape.center.y,
          shape.radius,
          shape.radius,
          0,
          0,
          Math.PI * 2
        );

        if (style.fillStyle != null) {
          ctx.fill();
        }
        if (style.strokeStyle != null) {
          ctx.stroke();
        }
        break;
      }
      case "lightbulb": {
        ctx.beginPath();
        ctx.ellipse(
          shape.center.x,
          shape.center.y,
          shape.radius,
          shape.radius,
          0,
          0,
          Math.PI * 2
        );

        const innerRadius = shape.radius + 4;
        const outerRadius = shape.radius + 8;
        for (let i = 0; i < 8; i++) {
          const spokeAngle = (Math.PI * 2 * i) / 8;

          ctx.moveTo(
            shape.center.x + Math.cos(spokeAngle) * innerRadius,
            shape.center.y + Math.sin(spokeAngle) * innerRadius
          );

          ctx.lineTo(
            shape.center.x + Math.cos(spokeAngle) * outerRadius,
            shape.center.y + Math.sin(spokeAngle) * outerRadius
          );
        }

        if (style.fillStyle != null) {
          ctx.fill();
        }
        if (style.strokeStyle != null) {
          ctx.stroke();
        }
        break;
      }
    }
  }

  const childrenComponent = root.children();
  if (childrenComponent != null) {
    for (let child of childrenComponent.children) {
      drawSceneHelper(child, ctx);
    }
  }
}
